import React, { useEffect, useState } from 'react';
import { Volume, StopCircle } from 'lucide-react';

interface ReadAloudProps {
  text: string;
  buttonLabel?: string;
  buttonClassName?: string;
}

/**
 * ReadAloud - Accessibility component that reads text content aloud
 * using the browser's Speech Synthesis API
 * 
 * @param text - The text content to be read aloud
 * @param buttonLabel - Optional custom label for the button
 * @param buttonClassName - Optional custom CSS class for styling
 */
export const ReadAloud: React.FC<ReadAloudProps> = ({
  text,
  buttonLabel = 'Read aloud',
  buttonClassName = ''
}) => {
  const [isReading, setIsReading] = useState(false);
  
  // Fix: Don't initialize synth in state, get it on demand
  // This resolves issues where speechSynthesis isn't fully initialized on load
  
  useEffect(() => {
    // Cleanup function to stop speech when component unmounts
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  
  const toggleReading = () => {
    // Make sure we have access to the speechSynthesis API
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.error('Speech synthesis not supported in this browser');
      alert('Text-to-speech is not supported in your browser');
      return;
    }
    
    if (isReading) {
      // Stop reading
      window.speechSynthesis.cancel();
      setIsReading(false);
      return;
    }
    
    // Start reading
    setIsReading(true);
    
    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Add end event listener
    utterance.addEventListener('end', () => setIsReading(false));
    utterance.addEventListener('error', (event) => {
      console.error('Speech synthesis error:', event);
      setIsReading(false);
    });
    
    // Try to use a neutral voice if available
    setTimeout(() => {
      const voices = window.speechSynthesis.getVoices();
      const neutralVoice = voices.find(voice => 
        voice.name.includes('Neutral') || 
        (!voice.name.toLowerCase().includes('female') && 
         !voice.name.toLowerCase().includes('male'))
      );
      
      if (neutralVoice) {
        utterance.voice = neutralVoice;
      }
      
      // Set other properties for better readability
      utterance.rate = 0.9; // Slightly slower rate for clarity
      utterance.pitch = 1.0; // Natural pitch
      utterance.volume = 1.0; // Full volume
      
      // Speak the text
      window.speechSynthesis.speak(utterance);
    }, 100); // Short delay to ensure voices are loaded
  };
  
  // Check if speech synthesis is supported
  const isSpeechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  
  if (!isSpeechSupported) {
    return (
      <button
        className={`flex items-center gap-2 py-1 px-3 text-sm rounded-md bg-red-50 text-red-700 border border-red-200 transition-colors ${buttonClassName}`}
        disabled
        title="Text-to-speech is not supported in your browser"
      >
        <Volume className="w-4 h-4" />
        <span>Not supported</span>
      </button>
    );
  }
  
  return (
    <button
      onClick={toggleReading}
      className={`flex items-center gap-2 py-1 px-3 text-sm rounded-md bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-200 transition-colors ${buttonClassName}`}
      aria-pressed={isReading}
      title={isReading ? 'Stop reading' : buttonLabel}
    >
      {isReading ? (
        <>
          <StopCircle className="w-4 h-4" />
          <span>Stop</span>
        </>
      ) : (
        <>
          <Volume className="w-4 h-4" />
          <span>{buttonLabel}</span>
        </>
      )}
    </button>
  );
};

export default ReadAloud; 